import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import { CheckCircle2, Clock, ExternalLink, Mail, ShieldCheck, XCircle } from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { isAuthenticated } from '../../store/selectors/AuthSelectors';
import AccountPage from '../layouts/AccountPage';
import Callout from '../components/Common/Callout';
import StatusBadge from '../components/Common/StatusBadge';

const amount = value => (value === undefined || value === null || value === '' ? '—' : value);

export default function McpAction() {
    const authenticated = useSelector(isAuthenticated), location = useLocation();
    const approval = new URLSearchParams(location.search).get('approval');
    const [preview, setPreview] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
    const [decision, setDecision] = useState(''), [recent, setRecent] = useState(false);
    const generation = useRef(0);
    useEffect(() => {
        const current = ++generation.current;
        setPreview(null); setError(''); setDecision(''); setRecent(false);
        if (authenticated) {
            if (!/^[a-f0-9-]{36}$/.test(approval || '')) setError('Länken är ogiltig. Börja om från din AI-app.');
            else {
                setBusy(true);
                axios.post(getApiUrl(`auth/mcp/actions/${approval}/preview`), {}).then(({ data }) => {
                    if (current === generation.current) setPreview(data);
                }).catch(() => { if (current === generation.current) setError('Åtgärden kan inte granskas. Den kan ha gått ut eller tillhöra ett annat konto.'); })
                    .finally(() => { if (current === generation.current) setBusy(false); });
            }
        }
        return () => { generation.current += 1; };
    }, [approval, authenticated]);
    if (!authenticated) return <Redirect to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />;
    const decide = async value => {
        const current = generation.current;
        setBusy(true); setError(''); setRecent(false);
        try {
            const { data } = await axios.post(getApiUrl(`auth/mcp/actions/${approval}/decision`), { decision: value });
            if (current === generation.current) { setDecision(data.status); setPreview(null); }
        } catch (failure) {
            if (current !== generation.current) return;
            const needsLogin = failure?.response?.data?.code === 'RECENT_LOGIN_REQUIRED';
            setRecent(needsLogin);
            setError(needsLogin ? 'Logga in igen för att godkänna åtgärden.' : 'Åtgärden kunde inte godkännas. Börja om från din AI-app.');
        } finally { if (current === generation.current) setBusy(false); }
    };
    const loginAgain = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    const payroll = preview?.tool === 'run_payroll';
    return <AccountPage width="narrow" back={{ to: '/connected-apps', label: 'Anslutna appar' }}>
        <div className="modern-card ll-consent">
            <div className="modern-card-body">
                <div className="ll-consent__hero">
                    <span className="ll-consent__glyph"><ShieldCheck size={28} strokeWidth={1.75} aria-hidden="true" /></span>
                    <h1 className="ll-consent__title">Granska åtgärd från AI-app</h1>
                    {preview
                        ? <><p className="ll-consent__lead"><strong>{preview.client.name}</strong> begär en åtgärd för <strong>{preview.company.name}</strong> via ditt konto.</p>
                            <span className="ll-consent__account"><Mail size={13} aria-hidden="true" />{preview.accountEmail}</span></>
                        : <p className="ll-consent__lead">Godkännandet gäller en gång och endast för de uppgifter du granskar här.</p>}
                </div>
                {busy && !preview && !error && !decision && <p role="status" className="text-center text-muted"><Spinner as="span" animation="border" size="sm" className="me-2" />Hämtar åtgärden…</p>}
                {error && <Callout tone="danger" role="alert">
                    <p>{error}</p>
                    {recent && <p><Link to={loginAgain} className="btn btn-sm btn-outline-primary mt-1">Logga in igen</Link></p>}
                </Callout>}
                {decision && <div className={`ll-result${decision === 'approved' ? '' : ' ll-result--muted'}`} role="status">
                    <span className="ll-result__glyph">{decision === 'approved' ? <CheckCircle2 size={28} aria-hidden="true" /> : <XCircle size={28} aria-hidden="true" />}</span>
                    <h2 className="ll-result__title">{decision === 'approved' ? 'Åtgärden är godkänd' : 'Åtgärden har avböjts'}</h2>
                    <p className="ll-result__body">{decision === 'approved'
                        ? 'Godkännandet är klart. Återvänd till AI-appen för att utföra åtgärden. Godkännandet gäller en gång och endast för uppgifterna du granskat.'
                        : 'Ingen åtgärd utförs. Du kan stänga den här sidan och återgå till AI-appen.'}</p>
                </div>}
                {preview && <>
                    <section className="ll-consent__section">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <h2 className="section-title mb-0">{payroll ? 'Lås lönekörning' : 'Bokför verifikation'}</h2>
                            <StatusBadge tone="warning">Väntar på ditt godkännande</StatusBadge>
                        </div>
                        {payroll ? <>
                            <dl className="ll-kv mb-3">
                                <div><dt>Lönekörning</dt><dd><strong>{preview.input.path.split('/')[2]}</strong></dd></div>
                                <div><dt>Företag</dt><dd>{preview.company.name}</dd></div>
                            </dl>
                            <p className="small text-muted">Lönekörningen låses när AI-appen utför åtgärden.</p>
                            <Link to={`/${preview.company.id}/payroll/periods`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                Granska lönekörningen i LedgerLoop <ExternalLink size={13} aria-hidden="true" /></Link>
                        </> : <>
                            <dl className="ll-kv mb-3">
                                <div><dt>Datum</dt><dd><strong>{preview.input.body.date}</strong></dd></div>
                                <div><dt>Serie</dt><dd>{preview.input.body.series || 'V'}</dd></div>
                                <div><dt>Beskrivning</dt><dd><strong>{preview.input.body.description}</strong></dd></div>
                            </dl>
                            <div className="ll-table-card">
                                <table className="ll-table">
                                    <thead><tr><th>Konto</th><th>Beskrivning</th><th className="ll-num">Debet</th><th className="ll-num">Kredit</th></tr></thead>
                                    <tbody>{(preview.input.body.entries || []).map((entry, index) => <tr key={index}>
                                        <td>{entry.accountNumber}</td><td>{entry.description || <span className="text-muted">—</span>}</td>
                                        <td className="ll-num">{amount(entry.debit)}</td><td className="ll-num">{amount(entry.credit)}</td>
                                    </tr>)}</tbody>
                                </table>
                            </div>
                        </>}
                    </section>
                    <Callout tone="warning">
                        <p>Appens namn anges av appens utgivare. Godkänn bara en åtgärd som du själv har bett om, och kontrollera uppgifterna ovan innan du fortsätter.</p>
                    </Callout>
                    <p className="ll-consent__expiry"><Clock size={13} aria-hidden="true" />{`Godkännandet upphör ${new Date(preview.expiresAt).toLocaleString('sv-SE')}.`}</p>
                    <div className="ll-consent__actions">
                        <Button variant="outline-secondary" disabled={busy} onClick={() => decide('deny')}>Avböj</Button>
                        <Button variant="primary" disabled={busy} onClick={() => decide('approve')}>Godkänn åtgärd</Button>
                    </div>
                </>}
            </div>
        </div>
        <p className="ll-consent__footer"><Link to="/connected-apps">Hantera anslutna appar</Link></p>
    </AccountPage>;
}
